import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { PROMOTIONS_API_URL } from './promotions-api.token';
import {
  ApiEnvelope,
  BranchDto,
  CouponCategoryRequestDto,
  CouponCategoryResponseDto,
  CouponRequestDto,
  CouponResponseDto,
  DiscountRequestDto,
  DiscountResponseDto,
  LowPriceQuickEditDto,
  LowPriceRequestDto,
  LowPriceResponseDto,
  OfferQuickEditDto,
  OfferRequestDto,
  OfferResponseDto,
  PromoTypeDto,
} from './promotions-api.types';

@Injectable({ providedIn: 'root' })
export class PromotionsApiService {
  private http = inject(HttpClient);
  private base = inject(PROMOTIONS_API_URL);

  private desenvolver<T>(fuente: Observable<ApiEnvelope<T>>): Observable<T> {
    return fuente.pipe(map((respuesta) => respuesta.data));
  }

  listarSucursales(): Observable<BranchDto[]> {
    return this.desenvolver(this.http.get<ApiEnvelope<BranchDto[]>>(`${this.base}/branches`));
  }

  listarDescuentos(): Observable<DiscountResponseDto[]> {
    return this.desenvolver(
      this.http.get<ApiEnvelope<DiscountResponseDto[]>>(`${this.base}/discounts`),
    );
  }

  crearDescuento(body: DiscountRequestDto): Observable<DiscountResponseDto> {
    return this.desenvolver(
      this.http.post<ApiEnvelope<DiscountResponseDto>>(`${this.base}/discounts`, body),
    );
  }

  actualizarDescuento(id: string, body: DiscountRequestDto): Observable<DiscountResponseDto> {
    return this.desenvolver(
      this.http.put<ApiEnvelope<DiscountResponseDto>>(`${this.base}/discounts/${id}`, body),
    );
  }

  cambiarEstadoDescuento(id: string, activo: boolean): Observable<DiscountResponseDto> {
    return this.desenvolver(
      this.http.patch<ApiEnvelope<DiscountResponseDto>>(
        `${this.base}/discounts/${id}/status?active=${activo}`,
        {},
      ),
    );
  }

  eliminarDescuento(id: string): Observable<void> {
    return this.desenvolver(this.http.delete<ApiEnvelope<void>>(`${this.base}/discounts/${id}`));
  }

  listarCategoriasCupon(): Observable<CouponCategoryResponseDto[]> {
    return this.desenvolver(
      this.http.get<ApiEnvelope<CouponCategoryResponseDto[]>>(`${this.base}/coupon-categories`),
    );
  }

  crearCategoriaCupon(body: CouponCategoryRequestDto): Observable<CouponCategoryResponseDto> {
    return this.desenvolver(
      this.http.post<ApiEnvelope<CouponCategoryResponseDto>>(`${this.base}/coupon-categories`, body),
    );
  }

  actualizarCategoriaCupon(
    id: string,
    body: CouponCategoryRequestDto,
  ): Observable<CouponCategoryResponseDto> {
    return this.desenvolver(
      this.http.put<ApiEnvelope<CouponCategoryResponseDto>>(
        `${this.base}/coupon-categories/${id}`,
        body,
      ),
    );
  }

  listarCupones(): Observable<CouponResponseDto[]> {
    return this.desenvolver(
      this.http.get<ApiEnvelope<CouponResponseDto[]>>(`${this.base}/coupons`),
    );
  }

  crearCupon(body: CouponRequestDto): Observable<CouponResponseDto> {
    return this.desenvolver(
      this.http.post<ApiEnvelope<CouponResponseDto>>(`${this.base}/coupons`, body),
    );
  }

  actualizarCupon(id: string, body: CouponRequestDto): Observable<CouponResponseDto> {
    return this.desenvolver(
      this.http.put<ApiEnvelope<CouponResponseDto>>(`${this.base}/coupons/${id}`, body),
    );
  }

  eliminarCupon(id: string): Observable<void> {
    return this.desenvolver(this.http.delete<ApiEnvelope<void>>(`${this.base}/coupons/${id}`));
  }

  listarOfertas(tipo: PromoTypeDto): Observable<OfferResponseDto[]> {
    return this.desenvolver(
      this.http.get<ApiEnvelope<OfferResponseDto[]>>(`${this.base}/offers?type=${tipo}`),
    );
  }

  crearOfertas(tipo: PromoTypeDto, body: OfferRequestDto): Observable<OfferResponseDto[]> {
    return this.desenvolver(
      this.http.post<ApiEnvelope<OfferResponseDto[]>>(`${this.base}/offers?type=${tipo}`, body),
    );
  }

  editarOferta(
    tipo: PromoTypeDto,
    id: string,
    body: OfferQuickEditDto,
  ): Observable<OfferResponseDto> {
    return this.desenvolver(
      this.http.patch<ApiEnvelope<OfferResponseDto>>(`${this.base}/offers/${id}?type=${tipo}`, body),
    );
  }

  eliminarOferta(tipo: PromoTypeDto, id: string): Observable<void> {
    return this.desenvolver(
      this.http.delete<ApiEnvelope<void>>(`${this.base}/offers/${id}?type=${tipo}`),
    );
  }

  listarPreciosBajos(): Observable<LowPriceResponseDto[]> {
    return this.desenvolver(
      this.http.get<ApiEnvelope<LowPriceResponseDto[]>>(`${this.base}/low-prices`),
    );
  }

  crearPreciosBajos(body: LowPriceRequestDto): Observable<LowPriceResponseDto[]> {
    return this.desenvolver(
      this.http.post<ApiEnvelope<LowPriceResponseDto[]>>(`${this.base}/low-prices`, body),
    );
  }

  editarPrecioBajo(id: string, body: LowPriceQuickEditDto): Observable<LowPriceResponseDto> {
    return this.desenvolver(
      this.http.patch<ApiEnvelope<LowPriceResponseDto>>(`${this.base}/low-prices/${id}`, body),
    );
  }

  eliminarPrecioBajo(id: string): Observable<void> {
    return this.desenvolver(this.http.delete<ApiEnvelope<void>>(`${this.base}/low-prices/${id}`));
  }
}
